import * as THREE from 'three';
import type { TowerModel, TowerType } from '../assets/modelFactories/TowerFactory';

export type TowerStats = {
  type: TowerType;
  cost: number;
  maxHealth: number;
  damage: number;
  fireRate: number;
  range: number;
  projectileSpeed: number;
  splashRadius: number;
  armorPiercing: boolean;
  description: string;
};

export const TOWER_STATS: Record<TowerType, TowerStats> = {
  mg: {
    type: 'mg',
    cost: 60,
    maxHealth: 60,
    damage: 7,
    fireRate: 8,
    range: 5.5,
    projectileSpeed: 22,
    splashRadius: 0,
    armorPiercing: false,
    description: '高射速机枪，适合清理步兵',
  },
  cannon: {
    type: 'cannon',
    cost: 120,
    maxHealth: 100,
    damage: 45,
    fireRate: 0.8,
    range: 7.5,
    projectileSpeed: 14,
    splashRadius: 1.4,
    armorPiercing: false,
    description: '榴弹炮，造成范围伤害',
  },
  at: {
    type: 'at',
    cost: 180,
    maxHealth: 120,
    damage: 75,
    fireRate: 0.55,
    range: 9,
    projectileSpeed: 18,
    splashRadius: 0,
    armorPiercing: true,
    description: '穿甲炮，专门对付坦克',
  },
};

export class Tower {
  readonly group: THREE.Group;
  readonly stats: TowerStats;
  readonly turret: THREE.Group;
  readonly muzzleSocket: THREE.Object3D;
  health: number;
  active = true;
  cooldown = 0;
  private readonly temp = new THREE.Vector3();

  constructor(
    type: TowerType,
    position: THREE.Vector3,
    model: TowerModel,
  ) {
    this.stats = TOWER_STATS[type];
    this.health = this.stats.maxHealth;
    this.group = model.root;
    this.turret = model.turret;
    this.muzzleSocket = model.muzzleSocket;
    this.group.position.copy(position);
    this.group.userData.tower = this;
  }

  update(delta: number, targetPosition: THREE.Vector3 | null): void {
    this.cooldown = Math.max(0, this.cooldown - delta);

    if (targetPosition) {
      this.temp.copy(targetPosition).sub(this.group.position);
      this.temp.y = 0;
      const angle = Math.atan2(this.temp.x, this.temp.z) + Math.PI;
      const current = this.turret.rotation.y;
      let diff = angle - current;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.turret.rotation.y += diff * Math.min(1, delta * 10);
    }
  }

  canFire(): boolean {
    return this.cooldown <= 0;
  }

  fire(): void {
    this.cooldown = 1 / this.stats.fireRate;
  }

  takeDamage(amount: number): void {
    if (!this.active) return;
    this.health -= amount;
    if (this.health <= 0) {
      this.active = false;
      this.group.visible = false;
    }
  }

  dispose(): void {
    this.group.clear();
  }
}
